import type { ReactNode, CSSProperties } from "react";
import type { Theme, FontPairing, BatchResult, Finding, FindingKind, BatchAnomaly } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { buildBatchIntelligence } from "../data/intelligence.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  batch: BatchResult | null;
  onBack: () => void;
  onExport: () => void;
  onReviewQueue: () => void;
}

export function StageBatchIntelligence({ theme, fonts, batch, onBack, onExport, onReviewQueue }: Props) {
  if (!batch) return null;
  const intel = buildBatchIntelligence(batch);
  if (!intel) return null;
  const { metrics, anomalies, findings, type_distribution } = intel;

  return (
    <div style={{ flex: 1, overflow: "auto", background: theme.bg }}>
      <style>{`
        @media (max-width: 760px) {
          .batch-intel-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(20px, 4vw, 48px)" }}>
        <div style={{ opacity: 0, animation: "fadeUp 500ms forwards" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
            <MonoLabel theme={theme} fonts={fonts} size={10}>BATCH INTELLIGENCE</MonoLabel>
            <div style={{ flex: 1, minWidth: 40, height: 1, background: theme.rule }} />
            <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>{batch.workflow.toUpperCase()}</MonoLabel>
          </div>
          <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: "clamp(24px, 4vw, 36px)", lineHeight: 1.1, letterSpacing: "-0.025em", color: theme.ink, margin: "14px 0 12px 0", maxWidth: 820 }}>
            {intel.summary}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 1, background: theme.rule, border: `1px solid ${theme.rule}`, marginTop: 28 }}>
          <Metric label="DOCUMENTS" value={String(metrics.total_documents)} theme={theme} fonts={fonts} />
          <Metric label="TOTAL CLAIMED" value={metrics.total_amount} theme={theme} fonts={fonts} />
          <Metric label="AVERAGE" value={metrics.avg_amount} theme={theme} fonts={fonts} />
          <Metric label="LARGEST" value={metrics.max_amount} sub={metrics.max_row.insured} theme={theme} fonts={fonts} />
          <Metric label="READY" value={`${metrics.ready_count} / ${metrics.total_documents}`} theme={theme} fonts={fonts} />
          <Metric label="FLAGGED" value={String(metrics.flagged_count)} tone={metrics.flagged_count > 0 ? "caution" : "neutral"} theme={theme} fonts={fonts} />
        </div>

        <div className="batch-intel-grid" style={{ display: "grid", gap: 32, marginTop: 36, gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)" }}>
          <div>
            <SectionHeader theme={theme} fonts={fonts}>FINDINGS</SectionHeader>
            {findings.map((f, i) => <BatchFindingCard key={i} finding={f} theme={theme} fonts={fonts} />)}
            <SectionHeader theme={theme} fonts={fonts} style={{ marginTop: 32 }}>ANOMALIES · {anomalies.length}</SectionHeader>
            {anomalies.map((a, i) => <BatchAnomalyCard key={i} anomaly={a} theme={theme} fonts={fonts} />)}
          </div>

          <div>
            <SectionHeader theme={theme} fonts={fonts}>LOSS-TYPE MIX</SectionHeader>
            <Distribution items={type_distribution} total={metrics.total_documents} theme={theme} fonts={fonts} />

            <SectionHeader theme={theme} fonts={fonts} style={{ marginTop: 32 }}>QUALITY</SectionHeader>
            <div style={{ padding: "14px 0", borderTop: `1px solid ${theme.rule}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontFamily: fonts.sans, fontSize: 28, fontWeight: 500, color: theme.ink, letterSpacing: "-0.02em" }}>
                  {Math.round(metrics.avg_confidence * 100)}%
                </span>
                <span style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkMute }}>average extraction confidence</span>
              </div>
              <div style={{ fontFamily: fonts.sans, fontSize: 12.5, color: theme.inkSoft, lineHeight: 1.5, marginTop: 8 }}>
                Above 90% means ready to export without review. Below 85% triggers a flag.
              </div>
            </div>

            <SectionHeader theme={theme} fonts={fonts} style={{ marginTop: 32 }}>NEXT</SectionHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {metrics.flagged_count > 0 && (
                <QuietButton theme={theme} fonts={fonts} onClick={onReviewQueue} style={{ width: "100%", justifyContent: "space-between", borderColor: theme.confidenceLow, color: theme.confidenceLow }}>
                  Review queue ({metrics.flagged_count}) →
                </QuietButton>
              )}
              <QuietButton theme={theme} fonts={fonts} primary onClick={onExport} style={{ width: "100%", justifyContent: "space-between" }}>
                Export all ready ({metrics.ready_count}) →
              </QuietButton>
              <QuietButton theme={theme} fonts={fonts} onClick={onBack} style={{ width: "100%", justifyContent: "space-between" }}>
                Back to results
              </QuietButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, tone, theme, fonts }: { label: string; value: string; sub?: string; tone?: "caution" | "neutral"; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ background: theme.surface, padding: "16px 18px" }}>
      <div style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontFamily: fonts.sans, fontSize: "clamp(20px, 2.4vw, 26px)", fontWeight: 500, color: tone === "caution" ? theme.confidenceLow : theme.ink, letterSpacing: "-0.02em", marginTop: 6, lineHeight: 1.05 }}>
        {value}
      </div>
      {sub && <div style={{ fontFamily: fonts.sans, fontSize: 11, color: theme.inkMute, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ children, theme, fonts, style }: { children: ReactNode; theme: Theme; fonts: FontPairing; style?: CSSProperties }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 14, ...style }}>
      <MonoLabel theme={theme} fonts={fonts} size={10}>{children}</MonoLabel>
      <div style={{ flex: 1, height: 1, background: theme.rule }} />
    </div>
  );
}

function findingKindColor(kind: FindingKind, theme: Theme): string {
  if (kind === "policy") return theme.accent;
  return theme.inkSoft;
}

function BatchFindingCard({ finding, theme, fonts }: { finding: Finding; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ padding: "14px 0", borderTop: `1px solid ${theme.rule}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.08em", color: findingKindColor(finding.kind, theme), fontWeight: 500, textTransform: "uppercase" }}>
          {finding.kind}
        </span>
      </div>
      <div style={{ fontFamily: fonts.sans, fontSize: 15, fontWeight: 500, color: theme.ink, letterSpacing: "-0.015em", marginBottom: 4, lineHeight: 1.3 }}>{finding.title}</div>
      <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, lineHeight: 1.5 }}>{finding.body}</div>
    </div>
  );
}

function BatchAnomalyCard({ anomaly, theme, fonts }: { anomaly: BatchAnomaly; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ padding: "12px 14px", borderTop: `1px solid ${theme.rule}`, display: "flex", gap: 10 }}>
      <span style={{ flexShrink: 0, color: theme.confidenceLow, fontFamily: fonts.mono, fontSize: 11, marginTop: 2 }}>⚠</span>
      <div>
        <div style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 500, color: theme.ink, letterSpacing: "-0.005em", lineHeight: 1.35, marginBottom: 4 }}>{anomaly.title}</div>
        {anomaly.body && <div style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkSoft, lineHeight: 1.5 }}>{anomaly.body}</div>}
      </div>
    </div>
  );
}

function Distribution({ items, total, theme, fonts }: { items: Array<[string, number]>; total: number; theme: Theme; fonts: FontPairing }) {
  return (
    <div>
      {items.map(([type, count]) => {
        const pct = count / total;
        return (
          <div key={type} style={{ padding: "10px 0", borderTop: `1px solid ${theme.rule}` }}>
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: 6 }}>
              <span style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, flex: 1, fontWeight: 450 }}>{type}</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkSoft }}>{count} · {Math.round(pct * 100)}%</span>
            </div>
            <div style={{ height: 3, background: theme.rule, position: "relative" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct * 100}%`, background: theme.ink, transition: "width 600ms cubic-bezier(.2,.7,.2,1)" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
